import React, { useState, useEffect, useContext } from "react";
import {
  Filter,
  Search,
  Eye,
  Edit,
  Download,
  RefreshCw,
  FileText,
  Calendar,
  DollarSign,
  Users,
  Building,
} from "lucide-react";
import { AuthContext } from "../../../context/AuthContext";
import { Link } from "react-router-dom";

const BillsManagement = ({ }) => {

  const {
    user,
    getAllBills,
    bills,
    billsLoading,
    billsError
  } = useContext(AuthContext);

  // Bills filtering state
  const [billsFilter, setBillsFilter] = useState({
    status: "PENDING",
    client: "ALL",
    project: "ALL",
    searchTerm: "",
    dateFrom: "",
    dateTo: "",
    amountMin: "",
    amountMax: "",
  });

  const [filteredBills, setFilteredBills] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // Filter bills based on filter criteria
  useEffect(() => {
    if (!bills || bills.length === 0) {
      setFilteredBills([]);
      return;
    }

    let filtered = [...bills];

    // Status filter
    if (billsFilter.status !== "ALL") {
      filtered = filtered.filter(bill => bill.status === billsFilter.status);
    }

    // Client filter
    if (billsFilter.client !== "ALL") {
      filtered = filtered.filter(bill => bill.client?._id === billsFilter.client);
    }

    // Project filter
    if (billsFilter.project !== "ALL") {
      filtered = filtered.filter(bill => bill.project?._id === billsFilter.project);
    }

    // Search term filter
    if (billsFilter.searchTerm) {
      const searchLower = billsFilter.searchTerm.toLowerCase();
      filtered = filtered.filter(bill =>
        bill.title?.toLowerCase().includes(searchLower) ||
        bill.description?.toLowerCase().includes(searchLower) ||
        bill.client?.companyName?.toLowerCase().includes(searchLower) ||
        bill.project?.name?.toLowerCase().includes(searchLower)
      );
    }

    // Date range filter
    if (billsFilter.dateFrom) {
      filtered = filtered.filter(bill =>
        new Date(bill.createdAt) >= new Date(billsFilter.dateFrom)
      );
    }
    if (billsFilter.dateTo) {
      filtered = filtered.filter(bill =>
        new Date(bill.createdAt) <= new Date(billsFilter.dateTo)
      );
    }

    // Amount range filter
    if (billsFilter.amountMin) {
      filtered = filtered.filter(bill =>
        parseFloat(bill.amount) >= parseFloat(billsFilter.amountMin)
      );
    }
    if (billsFilter.amountMax) {
      filtered = filtered.filter(bill =>
        parseFloat(bill.amount) <= parseFloat(billsFilter.amountMax)
      );
    }

    // Apply sorting
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        if (sortConfig.key === 'client') {
          aValue = a.client?.companyName || '';
          bValue = b.client?.companyName || '';
        } else if (sortConfig.key === 'project') {
          aValue = a.project?.name || '';
          bValue = b.project?.name || '';
        } else if (sortConfig.key === 'amount') {
          aValue = parseFloat(a.amount);
          bValue = parseFloat(b.amount);
        } else if (sortConfig.key === 'paidAmount') {
          aValue = calculatePaidAmount(a);
          bValue = calculatePaidAmount(b);
        } else if (sortConfig.key === 'outstanding') {
          aValue = calculateOutstanding(a);
          bValue = calculateOutstanding(b);
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    setFilteredBills(filtered);
  }, [bills, billsFilter, sortConfig]);

  // Get unique clients and projects for filter dropdowns
  const uniqueClients = bills ?
    bills
      .map(bill => bill.client)
      .filter(Boolean)
      .filter((client, index, array) =>
        array.findIndex(c => c._id === client._id) === index
      ) : [];

  useEffect(() => {
    console.log(bills)
  }, [])

  // Helper functions
  const getBillStatusColor = (status) => {
    switch (status) {
      case "PAID":
        return "bg-green-100 text-green-800 border-green-200";
      case "PENDING":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "OVERDUE":
        return "bg-red-100 text-red-800 border-red-200";
      case "CANCELLED":
        return "bg-gray-100 text-gray-800 border-gray-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatCurrency = (amount, currency = "INR") => {
    return `${parseFloat(amount).toLocaleString()} ${currency}`;
  };

  // Calculate paid amount for a bill
  const calculatePaidAmount = (bill) => {
    if (!bill.paidAmount || bill.paidAmount.length === 0) return 0;
    return bill.paidAmount.reduce((sum, payment) => sum + parseFloat(payment.amount), 0);
  };

  // Calculate outstanding amount for a bill
  const calculateOutstanding = (bill) => {
    const totalAmount = parseFloat(bill.amount);
    const paidAmount = calculatePaidAmount(bill);
    return Math.max(0, totalAmount - paidAmount);
  };

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setBillsFilter(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Handle sorting
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Reset filters
  const resetFilters = () => {
    setBillsFilter({
      status: "ALL",
      client: "ALL",
      project: "ALL",
      searchTerm: "",
      dateFrom: "",
      dateTo: "",
      amountMin: "",
      amountMax: "",
    });
    setSortConfig({ key: null, direction: 'asc' });
  };

  // Export functionality (placeholder)
  const handleExport = () => {
    console.log("Exporting bills data:", filteredBills);
    // Implement CSV/Excel export logic here
    alert("Export functionality would be implemented here");
  };

  // Calculate summary statistics for filtered bills
  const summaryStats = {
    totalBills: filteredBills.length,
    totalAmount: filteredBills.reduce((sum, bill) => sum + parseFloat(bill.amount), 0),
    totalPaid: filteredBills.reduce((sum, bill) => sum + calculatePaidAmount(bill), 0),
    totalOutstanding: filteredBills.reduce((sum, bill) => sum + calculateOutstanding(bill), 0),
    paidBills: filteredBills.filter(bill => bill.status === 'PAID').length,
    pendingBills: filteredBills.filter(bill => bill.status === 'PENDING').length,
    overdueBills: filteredBills.filter(bill => bill.status === 'OVERDUE').length,
  };

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center space-x-2">
            <FileText className="h-6 w-6 text-blue-600" />
            <h2 className="text-xl font-semibold text-gray-800">
              Bills Management
            </h2>
            {billsLoading && (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
            )}
          </div>

          <div className="flex items-center space-x-2">
            {(
              <button
                onClick={getAllBills}
                disabled={billsLoading}
                className="px-3 py-2 text-sm bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center space-x-1 disabled:opacity-50"
              >
                <RefreshCw size={16} className={billsLoading ? "animate-spin" : ""} />
                <span>Refresh</span>
              </button>
            )}
            <button
              onClick={resetFilters}
              className="px-4 py-2 text-sm bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center space-x-1"
            >
              <Filter size={16} />
              <span>Reset Filters</span>
            </button>
            <button
              onClick={handleExport}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-1"
            >
              <Download size={16} />
              <span>Export</span>
            </button>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <div className="bg-white rounded-lg p-3 border">
            <div className="flex items-center space-x-2">
              <FileText size={16} className="text-blue-600" />
              <div>
                <p className="text-xs font-medium text-gray-500">Total Bills</p>
                <p className="text-lg font-bold text-gray-900">{summaryStats.totalBills}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg p-3 border">
            <div className="flex items-center space-x-2">
              <DollarSign size={16} className="text-green-600" />
              <div>
                <p className="text-xs font-medium text-gray-500">Total Amount</p>
                <p className="text-lg font-bold text-gray-900">
                  {formatCurrency(summaryStats.totalAmount)}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg p-3 border">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              <div>
                <p className="text-xs font-medium text-gray-500">Total Paid</p>
                <p className="text-lg font-bold text-green-600">
                  {formatCurrency(summaryStats.totalPaid)}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg p-3 border">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-red-500 rounded-full"></div>
              <div>
                <p className="text-xs font-medium text-gray-500">Outstanding</p>
                <p className="text-lg font-bold text-red-600">
                  {formatCurrency(summaryStats.totalOutstanding)}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters Row 1 */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4 mb-4">
          {/* Search */}
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search bills..."
              value={billsFilter.searchTerm}
              onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
            {/* Status Filter */}
            <select
              value={billsFilter.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            >
              <option value="ALL">All Status</option>
              <option value="PAID">Paid</option>
              <option value="PENDING">Pending</option>
              <option value="OVERDUE">Overdue</option>
              <option value="CANCELLED">Cancelled</option>
            </select>

            {/* Client Filter */}
            <select
              value={billsFilter.client}
              onChange={(e) => handleFilterChange('client', e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            >
              <option value="ALL">All Clients</option>
              {uniqueClients.map(client => (
                <option key={client._id} value={client._id}>
                  {client.companyName}
                </option>
              ))}
            </select>
          </div>

          {/* Project Filter */}
          {/* <select
            value={billsFilter.project}
            onChange={(e) => handleFilterChange('project', e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
          >
            <option value="ALL">All Projects</option>
            {uniqueProjects.map(project => (
              <option key={project._id} value={project._id}>
                {project.name}
              </option>
            ))}
          </select> */}
        </div>

        {/* Filters Row 2 - Date and Amount */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="relative">
            <Calendar size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="date"
              placeholder="From Date"
              value={billsFilter.dateFrom}
              onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            />
          </div>
          <div className="relative">
            <Calendar size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="date"
              placeholder="To Date"
              value={billsFilter.dateTo}
              onChange={(e) => handleFilterChange('dateTo', e.target.value)}
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            />
          </div>
          <div className="relative">
            <DollarSign size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="number"
              placeholder="Min Amount"
              value={billsFilter.amountMin}
              onChange={(e) => handleFilterChange('amountMin', e.target.value)}
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            />
          </div>
          <div className="relative">
            <DollarSign size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="number"
              placeholder="Max Amount"
              value={billsFilter.amountMax}
              onChange={(e) => handleFilterChange('amountMax', e.target.value)}
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            />
          </div>
        </div>
      </div>

      {/* Bills Table */}
      <div className="overflow-x-auto">
        {billsLoading ? (
          <div className="flex items-center justify-center p-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-3 text-gray-600">Loading bills...</span>
          </div>
        ) : billsError ? (
          <div className="p-8 text-center">
            <div className="text-red-500 mb-2">Error loading bills:</div>
            <div className="text-gray-600">{billsError}</div>
          </div>
        ) : filteredBills.length === 0 ? (
          <div className="p-8 text-center">
            <FileText size={48} className="mx-auto text-gray-300 mb-4" />
            <div className="text-gray-500 text-lg">No bills found</div>
            <div className="text-gray-400 text-sm">
              {bills.length === 0 ? "No bills available" : "Try adjusting your filters"}
            </div>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('title')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Bill Details</span>
                    {sortConfig.key === 'title' && (
                      <span className="text-blue-600">
                        {sortConfig.direction === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('client')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Client/Project</span>
                    {sortConfig.key === 'client' && (
                      <span className="text-blue-600">
                        {sortConfig.direction === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('amount')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Amount</span>
                    {sortConfig.key === 'amount' && (
                      <span className="text-blue-600">
                        {sortConfig.direction === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('status')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Status</span>
                    {sortConfig.key === 'status' && (
                      <span className="text-blue-600">
                        {sortConfig.direction === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('dueDate')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Due Date</span>
                    {sortConfig.key === 'dueDate' && (
                      <span className="text-blue-600">
                        {sortConfig.direction === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredBills.map((bill) => (
                <tr key={bill._id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900 mb-1">
                      {bill.title}
                    </div>
                    <div className="text-sm text-gray-500 truncate max-w-xs cursor-pointer" title={bill.description}>
                      {bill.description}
                    </div>
                    <div className="text-xs text-gray-400 mt-1">
                      Created: {formatDate(bill.createdAt)}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-2 mb-1">
                      <Building size={14} className="text-gray-400" />
                      <div className="text-sm font-medium text-gray-900">
                        {bill.client?.companyName || 'N/A'}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <FileText size={14} className="text-gray-400" />
                      <div className="text-sm text-gray-500">
                        {bill.project?.name || 'No Project'}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900 mb-1">
                      {formatCurrency(bill.amount, bill.currency)}
                    </div>
                    <div className="text-sm text-green-600 mb-1">
                      Paid: {formatCurrency(calculatePaidAmount(bill), bill.currency)}
                    </div>
                    <div className="text-sm text-red-600">
                      Outstanding: {formatCurrency(calculateOutstanding(bill), bill.currency)}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full border ${getBillStatusColor(bill.status)}`}>
                      {bill.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {formatDate(bill.dueDate)}
                  </td>
                  <td className="px-6 py-4 text-sm font-medium">
                    <div className="flex items-center space-x-3">
                      <button
                        className="text-blue-600 hover:text-blue-900 transition-colors"
                        title="View Details"

                      >
                        <Link to={`/dashboard/billing-items/${bill._id}`}>
                          <Eye size={16} />
                        </Link>

                      </button>
                      <button
                        className="text-gray-600 hover:text-gray-900 transition-colors"
                        title="Edit Bill"
                      >
                        <Edit size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Table Footer */}
      {filteredBills.length > 0 && (
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center space-y-2 md:space-y-0">
            <div className="text-sm text-gray-600">
              Showing {filteredBills.length} of {bills.length} bills
            </div>
            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
              <span className="flex items-center space-x-1">
                <span className="font-medium">Total:</span>
                <span>{formatCurrency(summaryStats.totalAmount)}</span>
              </span>
              <span className="flex items-center space-x-1">
                <span className="font-medium text-green-600">Paid:</span>
                <span className="text-green-600">{formatCurrency(summaryStats.totalPaid)}</span>
              </span>
              <span className="flex items-center space-x-1">
                <span className="font-medium text-red-600">Outstanding:</span>
                <span className="text-red-600">{formatCurrency(summaryStats.totalOutstanding)}</span>
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BillsManagement;